import React, { useState, useEffect } from 'react';
import { X, Percent, DollarSign } from 'lucide-react';
import { applyDiscount } from '../lib/discounts';
import { useBusiness } from '../contexts/BusinessContext';

interface ApplyDiscountModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  bookingTotal: number;
  onApply: () => Promise<void>;
}

export default function ApplyDiscountModal({
  isOpen,
  onClose,
  bookingId,
  bookingTotal,
  onApply
}: ApplyDiscountModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    title: string;
    type: 'percentage' | 'fixed';
    amount: string;
  }>({
    title: '',
    type: 'percentage',
    amount: ''
  });

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
      setFormData({
        title: '',
        type: 'percentage',
        amount: ''
      });
    }
  };

  const calculateDiscountAmount = () => {
    const amount = parseFloat(formData.amount as string) || 0;
    if (formData.type === 'percentage') {
      // Round to 2 decimal places for consistent calculation
      const discountAmount = Math.round((bookingTotal * (amount / 100)) * 100) / 100;
      return Math.min(discountAmount, bookingTotal); // Prevent discount from exceeding total
    }
    return Math.min(amount, bookingTotal); // Prevent discount from exceeding total
  };

  const handleApplyDiscount = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      setError(null);

      await applyDiscount(
        bookingId,
        formData.title,
        formData.type,
        parseFloat(formData.amount) || 0
      );

      await onApply();
      onClose();
    } catch (err) {
      console.error('Error applying discount:', err);
      setError(err instanceof Error ? err.message : 'Failed to apply discount');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[999999]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } z-[999999]`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Apply Discount</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Name
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  placeholder="Enter discount name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Type
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={formData.type === 'percentage'}
                      onChange={() => setFormData(prev => ({ ...prev, type: 'percentage' }))}
                      className="h-4 w-4 text-gray-900 focus:ring-gray-900"
                    />
                    <span className="ml-2 text-sm text-gray-700">Percentage</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={formData.type === 'fixed'}
                      onChange={() => setFormData(prev => ({ ...prev, type: 'fixed' }))}
                      className="h-4 w-4 text-gray-900 focus:ring-gray-900"
                    />
                    <span className="ml-2 text-sm text-gray-700">Fixed Amount</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.type === 'percentage' ? 'Percentage' : 'Amount'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">
                      {formData.type === 'percentage' ? '%' : '$'}
                    </span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step={formData.type === 'percentage' ? '1' : '0.01'}
                    max={formData.type === 'percentage' ? "100" : undefined}
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      amount: e.target.value
                    }))}
                    className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  />
                </div>
              </div>

              {parseFloat(formData.amount as string) > 0 && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">
                    Discount amount: ${calculateDiscountAmount().toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    New total: ${(bookingTotal - calculateDiscountAmount()).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 bg-white">
            <button
              onClick={handleApplyDiscount}
              disabled={loading || !formData.title || !formData.amount || parseFloat(formData.amount) <= 0}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Applying...' : 'Apply Discount'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}